import React from 'react';
import { inject, observer } from 'mobx-react';

import { INPUT_TYPE, TRACKING_MSG_TYPE, TRACKING_TYPE, Transaction as TransactionTS } from '@/utils/constants';
import {
  ChatBtnType,
  StateTransactionListPropsType,
  TransactionItemType,
  TransactionListItemType,
  TransactionType,
} from '@/types/Transaction';
import { handleRequest } from '@/utils';
import { addDialogue } from '@/utils/sessionUtils';
import { classnames } from '@/utils/tools';
import { InitStoreType } from '@/types/store/init';
import { TrackerProps, ChatbotUIBasicProps } from '@/types/common';
import { USER_SEND_TYPE } from '@/types/history';
import { handleLiveBtnClick } from '@/utils/btnClickHandler';
import moreOrderImageCard from '@/assets/img/ic_cbot_topup@2x.png';
import moreOrderImageBank from '@/assets/img/ic_cbot_withdrawID@2x.png';
import { tracker } from '@/utils/tracking';
import { getChildInject, isNum, getTrans } from '@/utils/instantLoaded/tools';
import { getLiveAgentRecommenderInfo } from '@/utils/agent';

import TransactionList, { MoreTransactionListProps } from '../StateMoreTransactionList';
import Drawer from '../Drawer';
import SingleButton from '../SingleButton';
import Transaction from '../Transaction';
import { mapDataToTransaction } from '../Transaction/utils';

import './style.scss';

const PREFIX = 'transaction-list-container';

type Props = {
  initStore: InitStoreType;
  isHistory: boolean;
  customCallback?: ChatbotUIBasicProps['customCallback'];
} & StateTransactionListPropsType &
TrackerProps;

@inject(getChildInject('initStore'))
@observer
class StateTransactionList extends React.PureComponent<Props> {
  state = { drawerOpen: false };

  constructor(props: any) {
    super(props);
  }

  handleItemClick = (transaction: TransactionListItemType, index: number) => {
    const {
      dialogueId,
      initStore: { sessionId },
      type,
      tracker,
      nodePointId,
      isHistory,
    } = this.props;

    tracker.click({
      trackingname: TRACKING_TYPE.SHOPEE_PAY_TRANSACTION_CARD,
      data: {
        node_point_id: nodePointId,
        location: index,
        chat_session_id: sessionId || '',
        chat_dialogue_id: dialogueId,
        card_type: type === TransactionType.Topup ? 'topup' : 'withdraw',
        button_type: transaction.button_type,
        button_id: transaction.button_id,
        button_text: getTrans(TransactionTS.select, 'Select'),
        transaction_id: transaction.sn,
        if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
      },
    });

    this.handleTransactionItemClick(transaction);
  };

  handleShowDrawerClick = () => {
    const {
      dialogueId,
      initStore: { sessionId },
      list = [],
      type,
      tracker,
      nodePointId,
      moreButtonType,
      moreButtonId,
      isHistory,
    } = this.props;

    tracker.click({
      trackingname: TRACKING_TYPE.SHOPEE_PAY_OTHER_TRANSACTION,
      data: {
        node_point_id: nodePointId,
        location: list.length,
        chat_session_id: sessionId || '',
        chat_dialogue_id: dialogueId,
        card_type: type === TransactionType.Topup ? 'topup' : 'withdraw',
        button_type: moreButtonType,
        button_id: moreButtonId,
        button_text: getTrans(TransactionTS.more, 'Other Transactions'),
        if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
      },
    });

    this.setState({ drawerOpen: true });
  };

  handleNoMoreItemClick = () => {
    const {
      dialogueId,
      initStore: { sessionId },
      list = [],
      type,
      tracker,
      nodePointId,
      moreButtonId,
      moreButtonType,
      buttons_chat_main_location,
      isHistory,
    } = this.props;

    tracker.click({
      trackingname: TRACKING_TYPE.SHOPEE_PAY_DONT_SEE_BUTTON,
      data: {
        node_point_id: nodePointId,
        location: list.length,
        chat_session_id: sessionId || '',
        chat_dialogue_id: dialogueId,
        card_type: type === TransactionType.Topup ? 'topup' : 'withdraw',
        button_id: moreButtonId,
        button_type: moreButtonType,
        button_text: buttons_chat_main_location.message,
        if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
      },
    });

    this.handleSendMessage(buttons_chat_main_location);
  };

  handleDrawerItemClick = (transaction: TransactionItemType, index: number) => {
    const {
      dialogueId,
      initStore: { sessionId },
      type,
      tracker,
      nodePointId,
    } = this.props;

    tracker.click({
      trackingname: TRACKING_TYPE.SHOPEE_PAY_PANEL_TRANSACTION_CARD,
      data: {
        node_point_id: nodePointId,
        location: index,
        chat_session_id: sessionId || '',
        chat_dialogue_id: dialogueId,
        card_type: type === TransactionType.Topup ? 'topup' : 'withdraw',
        button_type: transaction.button_type,
        button_id: transaction.button_id,
      },
    });

    this.handleTransactionItemClick(transaction);
    this.setState({ drawerOpen: false });
  };

  handleDrawerFooterClick: MoreTransactionListProps['onFooterClick'] = (buttons_chat, trackingdata) => {
    const {
      dialogueId,
      initStore: { sessionId },
      type,
      tracker,
      nodePointId,
    } = this.props;

    tracker.click({
      trackingname: TRACKING_TYPE.SHOPEE_PAY_PANEL_DONT_SEE_BUTTON,
      data: {
        chat_session_id: sessionId || '',
        chat_dialogue_id: dialogueId,
        card_type: type === TransactionType.Topup ? 'topup' : 'withdraw',
        node_point_id: nodePointId,
        ...trackingdata,
      },
    });

    this.handleSendMessage(buttons_chat);
    this.setState({ drawerOpen: false });
  };

  handleTransactionItemClick = (transaction: TransactionItemType) => {
    const {
      dialogueId,
      dialogueType,
      node_id,
      initStore: { sessionId },
      type,
      customCallback = {},
    } = this.props;

    const props = mapDataToTransaction(type, transaction);

    const curPos = addDialogue({
      dialogueId,
      dialogueType,
      from: 'user',
      type: 'Transaction',
      data: {
        ...props,
        transReply: true,
      },
    });
    if (!isNum(curPos)) {
      return;
    }

    const sendType
      = type === TransactionType.Topup
        ? USER_SEND_TYPE.TRANSACTION_SELECT_TOPUP
        : USER_SEND_TYPE.TRANSACTION_SELECT_WITHDRAWL;
    const params: {
      transaction_sn: string;
      user_send: any;
    } = {
      transaction_sn: transaction.sn,
      user_send: {
        type: sendType,
        [sendType.toLowerCase()]: transaction,
      },
    };
    handleRequest('postMessage', {
      session_id: sessionId,
      dialogue_id: dialogueId,
      input_type: INPUT_TYPE.BUTTON,
      message_text: props.title,
      message_extra: {
        node_id,
        params,
      },
      ...customCallback,
    });
  };

  handleSendMessage = (buttons_chat: ChatBtnType & { message: string }) => {
    const {
      dialogueId,
      dialogueType,
      type,
      initStore: { sessionId },
      customCallback = {},
    } = this.props;
    const { queue_name, message: title } = buttons_chat;

    if (type === TransactionType.Topup) {
      // 回显
      addDialogue(
        {
          dialogueId,
          dialogueType,
          from: 'user',
          type: 'Msg',
          data: { content: buttons_chat.extra_param || buttons_chat.message },
        },
        false,
      );
      handleRequest('postMessage', {
        session_id: sessionId,
        dialogue_id: dialogueId,
        input_type: INPUT_TYPE.BUTTON,
        message_text: title,
        message_extra: { node_id: buttons_chat.node_id },
        ...customCallback,
      });
    } else {
      const { userType, enquiryType, triggerLiveAgentRecommender } = getLiveAgentRecommenderInfo({
        presetUserType: buttons_chat.user_type_id,
        presetEnquiryType: buttons_chat.enquiry_type_id,
      });

      handleLiveBtnClick({
        dialogueId,
        dialogueType,
        text: buttons_chat.message,
        queue_name,
        user_type: userType,
        enquiry_type: enquiryType,
        triggerLiveAgentRecommender,
      });
    }
  };

  renderTransactionItem = (transaction: any, index: number) => {
    const { type, initStore, dialogueId, passedRef, nodePointId, isHistory } = this.props;

    const trackingname = TRACKING_TYPE.SHOPEE_PAY_TRANSACTION_CARD;
    const trackingdata = {
      chat_session_id: initStore.sessionId || '',
      chat_dialogue_id: dialogueId,
      card_type: type === TransactionType.Topup ? 'topup' : 'withdraw',
      location: index,
      node_point_id: nodePointId,
      button_type: transaction.button_type,
      button_id: transaction.button_id,
      button_text: getTrans(TransactionTS.select, 'Select'),
      transaction_id: transaction.sn,
      if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
    };

    const props = mapDataToTransaction(type, transaction);

    return (
      <div
        className="order"
        key={`order-${index}`}
        ref={(ele) => passedRef(ele, index)}
        data-trackingname={trackingname}
        data-trackingindex={index}
        data-trackingdata={JSON.stringify({ ...trackingdata })}
      >
        <Transaction {...props} onClick={() => this.handleItemClick(transaction, index)} />
        <SingleButton
          handleClick={() => this.handleItemClick(transaction, index)}
          text={getTrans(TransactionTS.select, 'Select')}
        />
      </div>
    );
  };

  renderHasMoreBtn = () => {
    const { passedRef, list, initStore, type, dialogueId, nodePointId, moreButtonType, moreButtonId, isHistory }
      = this.props;

    const trackingname = TRACKING_TYPE.SHOPEE_PAY_OTHER_TRANSACTION;
    const trackingdata = {
      node_point_id: nodePointId,
      chat_session_id: initStore.sessionId || '',
      chat_dialogue_id: dialogueId,
      card_type: type === TransactionType.Topup ? 'topup' : 'withdraw',
      location: list?.length,
      button_type: moreButtonType,
      button_id: moreButtonId,
      button_text: getTrans(TransactionTS.more, 'Other Transactions'),
      if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
    };

    return (
      <div
        className="more-btn"
        ref={(ele) => passedRef(ele, list.length)}
        onClick={this.handleShowDrawerClick}
        data-trackingname={trackingname}
        data-trackingdata={JSON.stringify({ ...trackingdata })}
      >
        <div className="more-btn-container">
          <img src={type === TransactionType.Topup ? moreOrderImageCard : moreOrderImageBank} />
          <span>{getTrans(TransactionTS.more, 'Other Transactions')}</span>
        </div>
      </div>
    );
  };

  renderNoMoreBtn = () => {
    const {
      passedRef,
      list,
      initStore,
      dialogueId,
      type,
      buttons_chat_main_location,
      nodePointId,
      moreButtonId,
      moreButtonType,
      isHistory,
    } = this.props;

    const trackingname = TRACKING_TYPE.SHOPEE_PAY_DONT_SEE_BUTTON;
    const trackingdata = {
      node_point_id: nodePointId,
      chat_session_id: initStore.sessionId || '',
      chat_dialogue_id: dialogueId,
      card_type: type === TransactionType.Topup ? 'topup' : 'withdraw',
      button_id: moreButtonId,
      button_type: moreButtonType,
      button_text: buttons_chat_main_location.message,
      location: list?.length,
      if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
    };

    return (
      <div
        className="more-btn no-more"
        ref={(ele) => passedRef(ele, list.length)}
        onClick={this.handleNoMoreItemClick}
        data-trackingname={trackingname}
        data-trackingdata={JSON.stringify({ ...trackingdata, location: list?.length })}
      >
        <div className="more-btn-container">
          <span>{buttons_chat_main_location.message}</span>
        </div>
      </div>
    );
  };

  render() {
    const { drawerOpen } = this.state;
    const { list, dialogueId, has_more, type, buttons_chat_main_location, buttons_chat_vice_location, nodePointId }
      = this.props;

    return (
      <>
        <div className={classnames(PREFIX, {})}>
          <Drawer
            destroyOnClose
            show={drawerOpen}
            onClose={() => {
              this.setState({ drawerOpen: false });
            }}
          >
            <TransactionList
              dialogueId={dialogueId}
              type={type}
              buttons_for_live={buttons_chat_main_location}
              nodePointId={nodePointId}
              onClick={this.handleDrawerItemClick}
              onFooterClick={this.handleDrawerFooterClick}
            />
          </Drawer>
          {list.map((transaction, index) => this.renderTransactionItem(transaction, index))}
          {has_more ? this.renderHasMoreBtn() : this.renderNoMoreBtn()}
        </div>
        {buttons_chat_vice_location?.message && (
          <div className="transaction-consult">
            {buttons_chat_vice_location?.extra_param}
            <span
              onClick={() =>
                this.handleDrawerFooterClick?.(buttons_chat_vice_location, {
                  button_id: buttons_chat_vice_location.button_id,
                  button_type: buttons_chat_vice_location.button_type,
                  button_text: buttons_chat_vice_location.message,
                })
              }
            >
              {buttons_chat_vice_location?.message}
            </span>
          </div>
        )}
      </>
    );
  }
}

export default tracker(StateTransactionList);
